// The in-memory API's "My subscriptions" (BYO-01; services/api/src/routes/model-credentials.ts and
// connections.ts), with the real routes' rules: the platform switches (here the seed's demo
// policy: personal and company API keys on, everything else off) AND the company flags
// (`company.settings.flags`) gate each path; a key is never echoed, only its last four; a plan
// login holds no token and is refused while its switches are off; the paid-client-work path is
// refused; company scope is for managers; another person's rows are 404; a revoked row is 409.
// The API key check answers like the local fake vendor (services/api/src/testing/fake-vendor.ts):
// the fixture keys decide, nothing leaves the process. OAuth providers are "needs admin setup".
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, page, problem, validation } from "./fake";
import { crewMembershipIdOf, uid } from "./fake-data";

type Credential = OperationOutput<"modelCredentials.list">["data"][number];
type Attestation = OperationOutput<"modelCredentials.attest">;
type Connection = OperationOutput<"connections.list">["data"][number];
type Policy = OperationOutput<"subscriptionPolicy.get">;
type Provider = OperationOutput<"connections.providers">["data"][number];
type Principal = NonNullable<Parameters<Handler>[0]["principal"]>;
type CrewView = OperationOutput<"crews.connections">;
type CrewGrant = NonNullable<CrewView["mail"]["grant"]>;

/** The seed's demo policy (services/api/src/seed/subscriptions.ts). */
const PLATFORM: Policy["platform"] = {
  person_api_keys: true,
  company_api_keys: true,
  person_cli_subscriptions: false,
  person_key_for_company_work: false,
  owner_preapproved_fallback: false,
  openai_sign_in_with_chatgpt: false,
  deployment_engineer_subscription: false,
};
/** The fake vendor's fixture keys (test values). */
const VENDOR_KEYS = {
  good: "cos_test_tool_ok_7c41",
  expired: "cos_test_tool_expired_19ad",
  limited: "cos_test_tool_limited_5be2",
} as const;
const CLAUDE_AI_TOKEN = /\bsk-ant-(?:oat|ort|sid)\d*-/i;

const PROVIDERS: Provider[] = [
  {
    key: "api_key",
    kind: "api_key",
    name: "Custom API key",
    summary: "Any HTTPS API with a key — one host, one agent, every call approved",
    configured: true,
    setup_env: [],
    tiers: [],
    always_not: [],
  },
  {
    key: "google",
    kind: "oauth",
    name: "Google",
    summary: "Gmail · Calendar · Drive — read your calendar and files, send email with approval",
    configured: false,
    setup_env: ["COS_OAUTH_GOOGLE_CLIENT_ID", "COS_OAUTH_GOOGLE_CLIENT_SECRET"],
    tiers: [
      {
        key: "calendar_read",
        label: "Calendar: read",
        gives: "See your calendar events",
        does_not: "It cannot see your calendar.",
        note: null,
        founder_gated: false,
      },
      {
        key: "gmail_read",
        label: "Gmail: read",
        gives: "Read your email messages and settings",
        does_not: "It cannot read your email.",
        note: "Needs Google's security review before it can be offered.",
        founder_gated: true,
      },
    ],
    always_not: [],
  },
  {
    key: "github",
    kind: "oauth",
    name: "GitHub",
    summary: "Issues and pull requests — read, comment and open issues with approval",
    configured: false,
    setup_env: [
      "COS_OAUTH_GITHUB_CLIENT_ID",
      "COS_OAUTH_GITHUB_CLIENT_SECRET",
      "COS_GITHUB_APP_SLUG",
    ],
    tiers: [
      {
        key: "issues_prs",
        label: "Issues and pull requests in repositories you choose",
        gives: "Read issues, pull requests and their reviews, and write issues and comments",
        does_not: "It cannot reach your repositories.",
        note: "Never reads code diffs or file contents.",
        founder_gated: false,
      },
    ],
    always_not: ["It never reads code diffs or file contents."],
  },
  {
    key: "slack",
    kind: "oauth",
    name: "Slack",
    summary: "Public channels — read channels the bot is invited to, post with approval",
    configured: false,
    setup_env: ["COS_OAUTH_SLACK_CLIENT_ID", "COS_OAUTH_SLACK_CLIENT_SECRET"],
    tiers: [
      {
        key: "read",
        label: "Read public channels",
        gives:
          "View messages and basic information in public channels the CompanyOS app is invited to",
        does_not: "It cannot read any channel.",
        note: null,
        founder_gated: false,
      },
    ],
    always_not: ["It never reads or sends direct messages.", "It never reads private channels."],
  },
];

const forbidden = (detail: string) => problem(403, "forbidden", "Forbidden", detail);
const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);
const disabled = (detail: string) =>
  problem(
    403,
    "credential_path_disabled",
    "This credential path is not enabled on the platform (founder decision pending)",
    detail,
  );

export function createSubscriptionHandlers(deps: {
  now: () => Date;
  /**
   * M4-04: the demo person whose Google connections exist from before (connected while an OAuth
   * app was set up): one healthy (calendar read), one that needs signing in again. The providers
   * still say "Needs admin setup", so Refresh and Reconnect answer `connection_not_set_up`, as
   * the real API does without the founder's OAuth app.
   */
  demo?: { companyId: string; ownerUserId: string; email: string };
}): Partial<Record<RouteId, Handler>> {
  const credentials = new Map<string, Credential[]>();
  const attestations = new Map<string, Attestation[]>();
  const connections = new Map<string, Connection[]>();
  /** The fake's stand-in for the sealed secret: kept out of every response. */
  const sealed = new Map<string, string>();
  /** M4-04: crew mail and calendar grants per company. */
  const crewGrants = new Map<string, CrewGrant[]>();
  let seq = 0;
  const at = () => deps.now().toISOString();
  const listOf = <T>(m: Map<string, T[]>, co: string) => {
    const l = m.get(co) ?? [];
    m.set(co, l);
    return l;
  };
  if (deps.demo) {
    const d = deps.demo;
    const t = new Date(Date.UTC(2026, 9, 19, 8, 0)).toISOString();
    // A healthy check is "healthy" for 7 days only (`healthKey`), so the seed's last verification
    // follows the clock (an hour ago) instead of a fixed date that runs out a week later.
    const verified = new Date(deps.now().getTime() - 3_600_000).toISOString();
    const google = (n: number, label: string, state: Connection["state"]): Connection => ({
      id: uid(0x9c40, n),
      scope: "person",
      company_id: d.companyId,
      owner_user_id: d.ownerUserId,
      provider: "google",
      label,
      config: null,
      tiers: ["calendar_read"],
      identity_display: d.email,
      key_last4: null,
      state,
      last_error_class: state === "healthy" ? null : "reauth",
      last_verified_at: state === "healthy" ? verified : null,
      last_checked_at: t,
      token_expires_at: new Date(Date.UTC(2026, 9, 19, 9, 0)).toISOString(),
      last_refreshed_at: t,
      rate_limited_until: null,
      created_by_user_id: d.ownerUserId,
      revoked_at: null,
      revoked_by_user_id: null,
      created_at: t,
      updated_at: t,
    });
    listOf(connections, d.companyId).push(
      google(1, "Google calendar", "healthy"),
      google(2, "Google (old sign-in)", "reauth_required"),
    );
    // Agents UX pass 2026-10-21: one company-scope tool connection (an API key the agents use
    // within a run's scope), so Agents › Connections has a row; its check answers healthy.
    const ats: Connection = {
      id: uid(0x9c40, 3),
      scope: "company",
      company_id: d.companyId,
      owner_user_id: null,
      provider: "api_key",
      label: "Greenhouse (ATS)",
      config: {
        name: "GREENHOUSE",
        host: "harvest.greenhouse.io",
        position: { in: "header", name: "Authorization", scheme: "Basic" },
        probe_path: "/v1/users",
      },
      tiers: [],
      identity_display: null,
      key_last4: VENDOR_KEYS.good.slice(-4),
      state: "healthy",
      last_error_class: null,
      last_verified_at: verified,
      last_checked_at: t,
      token_expires_at: null,
      last_refreshed_at: null,
      rate_limited_until: null,
      created_by_user_id: d.ownerUserId,
      revoked_at: null,
      revoked_by_user_id: null,
      created_at: t,
      updated_at: t,
    };
    listOf(connections, d.companyId).push(ats);
    sealed.set(ats.id, VENDOR_KEYS.good);
  }
  const userOf = (p: Principal) => p.me.user.id;
  const mayManage = (p: Principal, r: { scope: string; owner_user_id: string | null }) =>
    r.scope === "person" ? r.owner_user_id === userOf(p) : p.manager;
  const visible = <T extends { scope: string; owner_user_id: string | null }>(
    p: Principal,
    rows: T[],
    scope: string | null,
  ) => rows.filter((r) => mayManage(p, r) && (!scope || r.scope === scope));

  function flagsOf(p: Principal): Policy["company"] {
    const f = p.company.settings.flags ?? {};
    return {
      model_api_keys: f.byo_model_provider_keys === true,
      model_cli_subscriptions: f.byo_model_cli_subscriptions === true,
      tool_connections: f.byo_tool_connections === true,
      crew_mail_calendar: f.crew_mail_calendar === true,
    };
  }
  function policyOf(p: Principal): Policy {
    const c = flagsOf(p);
    return {
      platform: PLATFORM,
      company: c,
      effective: {
        person_api_keys: PLATFORM.person_api_keys && c.model_api_keys,
        company_api_keys: PLATFORM.company_api_keys && c.model_api_keys,
        person_cli_subscriptions: PLATFORM.person_cli_subscriptions && c.model_cli_subscriptions,
        tool_connections: c.tool_connections,
        crew_mail_calendar: c.tool_connections && c.crew_mail_calendar,
      },
      can_manage: p.manager,
    };
  }

  function attest(p: Principal, cred: Credential, a: Record<string, unknown>): Attestation {
    const t = at();
    for (const live of listOf(attestations, cred.id))
      if (!live.revoked_at) Object.assign(live, { revoked_at: t, revoke_reason: "superseded" });
    const row: Attestation = {
      id: uid(0x9b00, ++seq),
      credential_id: cred.id,
      provider: cred.provider,
      plan: typeof a.plan === "string" ? a.plan : cred.plan,
      terms_ref: String(a.terms_ref ?? ""),
      terms_version: typeof a.terms_version === "string" ? a.terms_version : null,
      plan_is_own: a.plan_is_own === true,
      own_work_only: a.own_work_only === true,
      allows_paid_client_work: a.allows_paid_client_work === true,
      attested_by_user_id: userOf(p),
      attested_at: t,
      revoked_at: null,
      revoke_reason: null,
    };
    listOf(attestations, cred.id).push(row);
    return row;
  }

  /** The real rules (domain/subscriptions/rules.ts), condensed. */
  function refusal(p: Principal, b: Record<string, unknown>): Response | null {
    const a = (b.attestation ?? {}) as Record<string, unknown>;
    const pol = policyOf(p);
    if (typeof a.terms_ref !== "string" || !a.terms_ref)
      return validation("attestation.terms_ref: required");
    if (b.scope === "company" && !p.manager)
      return forbidden("Only owners and admins add company keys");
    if (b.kind === "api_key") {
      const open =
        b.scope === "company" ? pol.effective.company_api_keys : pol.effective.person_api_keys;
      if (!open) return disabled("API keys are not turned on here");
      const key = String(b.api_key ?? "").trim();
      if (key.length < 8 || /\s/.test(key))
        return validation("api_key: one line, at least 8 characters");
      if (CLAUDE_AI_TOKEN.test(key))
        return validation(
          "api_key: This is a Claude.ai subscription token, not an API key. Create an API key in the Claude Console instead",
        );
    } else if (b.kind === "cli_subscription") {
      if (b.scope !== "person") return validation("scope: a plan login is a person's own");
      if (!pol.effective.person_cli_subscriptions)
        return disabled(
          "Linking a plan login is off until the founder decides (OPEN-QUESTIONS 46–47) and the company turns it on",
        );
    } else return validation("kind: api_key or cli_subscription");
    if (a.plan_is_own !== true)
      return problem(
        422,
        "attestation_required",
        "The provider terms must be attested",
        "Confirm the key is your own",
      );
    if (
      a.allows_paid_client_work === true &&
      (b.kind === "cli_subscription" || b.scope === "person")
    )
      return disabled(
        "A person's own key or plan never powers paid client work (OPEN-QUESTIONS 46, 48)",
      );
    if (b.scope === "person" && a.own_work_only !== true)
      return problem(
        422,
        "attestation_required",
        "The provider terms must be attested",
        "Confirm it is used only for your own work",
      );
    return null;
  }

  function probe(key: string): {
    ok: boolean;
    cls: string | null;
    status: number;
    state: Connection["state"];
  } {
    if (key === VENDOR_KEYS.good) return { ok: true, cls: null, status: 200, state: "healthy" };
    if (key === VENDOR_KEYS.limited)
      return { ok: false, cls: "rate_limited", status: 429, state: "rate_limited" };
    return { ok: false, cls: "reauth", status: 401, state: "reauth_required" };
  }

  // ---- M4-04: mail and calendar for crews (domain/connections/crew.ts, condensed) --------------

  /** `crews/me/*`: the `agents` add-on, then the caller's own person membership and crew. */
  function crewOwn(
    run: (p: Principal, crew: string, owner: string, body: unknown) => Response,
  ): Handler {
    return ({ principal: p, body }) => {
      if (!p) return notFound();
      const map = (p.me.entitlements[p.company.id] ?? {}) as Record<string, string | undefined>;
      const on = (k: string) => ["active", "trial", "grace"].includes(map[k] ?? "disabled");
      if (!on("agents") || !on("memory"))
        return problem(
          403,
          "feature_disabled",
          "Feature not enabled",
          "The agents add-on is not enabled for this company.",
        );
      const m = p.me.memberships.find((x) => x.company_id === p.company.id && x.kind === "person");
      if (!m) return forbidden("only a person's own session manages a crew");
      return run(p, crewMembershipIdOf(p.company.id, m.membership_id), m.membership_id, body);
    };
  }

  function crewView(p: Principal, crew: string): CrewView {
    const f = flagsOf(p);
    const flagOn = f.tool_connections && f.crew_mail_calendar;
    const mine = listOf(connections, p.company.id).filter(
      (c) =>
        c.scope === "person" &&
        c.owner_user_id === userOf(p) &&
        c.provider === "google" &&
        c.state !== "disconnected",
    );
    const access = (kind: "mail" | "calendar"): CrewView["mail"] => {
      const tiers = kind === "mail" ? ["gmail_read"] : ["calendar_read", "calendar_write"];
      const grant =
        listOf(crewGrants, p.company.id).find(
          (g) => g.crew_membership_id === crew && g.kind === kind && !g.revoked_at,
        ) ?? null;
      const eligible = mine.filter((c) => c.tiers.some((t) => tiers.includes(t)));
      const granted = grant ? mine.find((c) => c.id === grant.connection_id) : undefined;
      const works = granted && (granted.state === "healthy" || granted.state === "rate_limited");
      return {
        kind,
        grant,
        eligible: eligible.map((c) => ({
          connection_id: c.id,
          label: c.label,
          identity_display: c.identity_display,
          state: c.state,
        })),
        blocked: !flagOn
          ? "flag_off"
          : kind === "mail" && !grant
            ? "founder_gated"
            : grant && !works
              ? "connection_unusable"
              : !grant && eligible.length === 0
                ? "no_connection"
                : null,
      };
    };
    return { mail: access("mail"), calendar: access("calendar") };
  }

  return {
    "subscriptionPolicy.get": ({ principal: p }) => (p ? json(policyOf(p)) : notFound()),

    "modelCredentials.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const rows = visible(p, listOf(credentials, p.company.id), url.searchParams.get("scope"));
      return json(page(rows, url));
    },
    "modelCredentials.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      const bad = refusal(p, b);
      if (bad) return bad;
      const t = at();
      const key = b.kind === "api_key" ? String(b.api_key).trim() : null;
      const cred: Credential = {
        id: uid(0x9a00, ++seq),
        scope: b.scope as Credential["scope"],
        kind: b.kind as Credential["kind"],
        company_id: p.company.id,
        owner_user_id: b.scope === "person" ? userOf(p) : null,
        provider: String(b.provider ?? ""),
        label: String(b.label ?? "").trim(),
        plan:
          typeof b.plan === "string"
            ? b.plan
            : typeof (b.attestation as Record<string, unknown>)?.plan === "string"
              ? ((b.attestation as Record<string, unknown>).plan as string)
              : null,
        key_last4: key ? key.slice(-4) : null,
        login_location:
          b.kind === "cli_subscription" ? (b.login_location as Credential["login_location"]) : null,
        runtime_ref: typeof b.runtime_ref === "string" ? b.runtime_ref : null,
        fallback_to_gateway: b.fallback_to_gateway === true,
        state: "active",
        last_verified_at: null,
        created_by_user_id: userOf(p),
        revoked_at: null,
        revoked_by_user_id: null,
        created_at: t,
        updated_at: t,
      };
      listOf(credentials, p.company.id).push(cred);
      const attestation = attest(p, cred, (b.attestation ?? {}) as Record<string, unknown>);
      return json({ credential: cred, attestation }, 201);
    },
    "modelCredentials.attest": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const cred = listOf(credentials, p.company.id).find((c) => c.id === params.id);
      if (!cred || !mayManage(p, cred)) return notFound();
      if (cred.state !== "active") return conflict("This credential is revoked");
      const a = (body ?? {}) as Record<string, unknown>;
      if (a.plan_is_own !== true)
        return problem(422, "attestation_required", "The provider terms must be attested");
      return json(attest(p, cred, a), 201);
    },
    "modelCredentials.revoke": ({ principal: p, params }) => {
      if (!p) return notFound();
      const cred = listOf(credentials, p.company.id).find((c) => c.id === params.id);
      if (!cred || !mayManage(p, cred)) return notFound();
      if (cred.state !== "active") return conflict("This credential is already revoked");
      const t = at();
      Object.assign(cred, {
        state: "revoked",
        revoked_at: t,
        revoked_by_user_id: userOf(p),
        updated_at: t,
      });
      for (const live of listOf(attestations, cred.id))
        if (!live.revoked_at)
          Object.assign(live, { revoked_at: t, revoke_reason: "credential revoked" });
      return json(cred);
    },
    // ACTIONS: the non-secret fields only (never a key); plan-login fields on a plan login only.
    "modelCredentials.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const cred = listOf(credentials, p.company.id).find((c) => c.id === params.id);
      if (!cred || !mayManage(p, cred)) return notFound();
      if (cred.state !== "active") return conflict("This credential is revoked");
      const b = (body ?? {}) as Record<string, unknown>;
      const keys = ["label", "login_location", "runtime_ref", "fallback_to_gateway"] as const;
      if (!keys.some((k) => b[k] !== undefined)) return validation("at least one field");
      const cliField = keys.slice(1).find((k) => b[k] !== undefined);
      if (cliField && cred.kind !== "cli_subscription")
        return validation(`${cliField}: only a plan login has this field`);
      if (b.label !== undefined) {
        if (typeof b.label !== "string" || !b.label.trim()) return validation("label: required");
        if (/sk-[A-Za-z0-9-]{8,}/.test(b.label))
          return validation("label: This looks like a token");
        cred.label = b.label.trim();
      }
      if (b.login_location !== undefined)
        cred.login_location = b.login_location as typeof cred.login_location;
      if (b.runtime_ref !== undefined) cred.runtime_ref = (b.runtime_ref as string | null) ?? null;
      if (typeof b.fallback_to_gateway === "boolean")
        cred.fallback_to_gateway = b.fallback_to_gateway;
      cred.updated_at = at();
      return json(cred);
    },

    "connections.providers": ({ principal: p }) => (p ? json({ data: PROVIDERS }) : notFound()),
    "connections.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const rows = visible(p, listOf(connections, p.company.id), url.searchParams.get("scope"));
      return json(page(rows, url));
    },
    "connections.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      const b = (body ?? {}) as Record<string, unknown>;
      if (!flagsOf(p).tool_connections)
        return problem(
          403,
          "connection_path_disabled",
          "Tool connections are not turned on for this company",
        );
      if (b.scope === "company" && !p.manager)
        return forbidden("Only owners and admins add company connections");
      const cfg = (b.config ?? {}) as Record<string, unknown>;
      const host = String(cfg.host ?? "")
        .trim()
        .toLowerCase();
      if (
        !/^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]*[a-z0-9]$/.test(host) ||
        /(^|\.)(localhost|local|internal)$/.test(host)
      )
        return validation("config: Host must be a public domain name such as api.example.com");
      if (!/^[A-Z0-9_]{2,40}$/.test(String(cfg.name ?? "")))
        return validation("config: Name must be 2–40 characters of A–Z, 0–9 and _");
      const key = String(b.api_key ?? "").trim();
      if (key.length < 8 || /\s/.test(key))
        return validation("api_key: one line, at least 8 characters");
      const t = at();
      const row: Connection = {
        id: uid(0x9c00, ++seq),
        scope: b.scope as Connection["scope"],
        company_id: p.company.id,
        owner_user_id: b.scope === "person" ? userOf(p) : null,
        provider: "api_key",
        label: String(b.label ?? "").trim(),
        config: {
          name: String(cfg.name),
          host,
          position: cfg.position as NonNullable<Connection["config"]>["position"],
          probe_path: String(cfg.probe_path ?? "/"),
        },
        tiers: [],
        identity_display: null,
        key_last4: key.slice(-4),
        state: "healthy",
        last_error_class: null,
        last_verified_at: null,
        last_checked_at: null,
        token_expires_at: null,
        last_refreshed_at: null,
        rate_limited_until: null,
        created_by_user_id: userOf(p),
        revoked_at: null,
        revoked_by_user_id: null,
        created_at: t,
        updated_at: t,
      };
      sealed.set(row.id, key);
      listOf(connections, p.company.id).push(row);
      return json(row, 201);
    },
    "connections.check": ({ principal: p, params }) => {
      if (!p) return notFound();
      const row = listOf(connections, p.company.id).find((c) => c.id === params.id);
      if (!row || !mayManage(p, row)) return notFound();
      if (row.state === "disconnected")
        return problem(409, "connection_state_conflict", "This connection is disconnected");
      const r = probe(sealed.get(row.id) ?? "");
      const t = at();
      Object.assign(row, {
        state: r.state,
        last_error_class: r.cls,
        last_checked_at: t,
        ...(r.ok ? { last_verified_at: t } : {}),
        updated_at: t,
      });
      return json({ connection: row, result: { ok: r.ok, error_class: r.cls, status: r.status } });
    },
    "connections.revoke": ({ principal: p, params }) => {
      if (!p) return notFound();
      const row = listOf(connections, p.company.id).find((c) => c.id === params.id);
      if (!row || !mayManage(p, row)) return notFound();
      if (row.state === "disconnected")
        return problem(409, "connection_state_conflict", "This connection is disconnected");
      const t = at();
      sealed.delete(row.id);
      Object.assign(row, {
        state: "disconnected",
        revoked_at: t,
        revoked_by_user_id: userOf(p),
        updated_at: t,
      });
      // M4-04: a crew reading through it stops.
      for (const g of listOf(crewGrants, p.company.id))
        if (g.connection_id === row.id && !g.revoked_at)
          Object.assign(g, { revoked_at: t, updated_at: t });
      return json(row);
    },
    // M4-04: no OAuth app on this server (as the demo), so a refresh cannot happen.
    "connections.refresh": ({ principal: p, params }) => {
      if (!p) return notFound();
      const row = listOf(connections, p.company.id).find((c) => c.id === params.id);
      if (!row || !mayManage(p, row)) return notFound();
      if (row.provider === "api_key" || row.state === "disconnected")
        return problem(
          409,
          "connection_state_conflict",
          "This connection is disconnected",
          "Only a live OAuth connection has a token to refresh",
        );
      if (row.state === "reauth_required" || row.state === "revoked")
        return problem(
          409,
          "connection_state_conflict",
          "This connection is disconnected",
          "The service no longer accepts this sign-in: reconnect it",
        );
      return problem(
        409,
        "connection_not_set_up",
        "This provider needs admin setup (the OAuth app credentials) before anyone connects it",
      );
    },
    "crews.connections": crewOwn((p, crew) => json(crewView(p, crew))),
    "crews.putConnections": crewOwn((p, crew, owner, body) => {
      const b = (body ?? {}) as Record<string, unknown>;
      const extra = Object.keys(b).filter((k) => k !== "mail" && k !== "calendar");
      if (extra.length || !("mail" in b) || !("calendar" in b))
        return validation("body: exactly mail and calendar, each a connection id or null");
      const view = crewView(p, crew);
      const wanted = { mail: b.mail as string | null, calendar: b.calendar as string | null };
      const grants = listOf(crewGrants, p.company.id);
      const live = (k: "mail" | "calendar") =>
        grants.find((g) => g.crew_membership_id === crew && g.kind === k && !g.revoked_at);
      const changing = (["mail", "calendar"] as const).filter(
        (k) => (live(k)?.connection_id ?? null) !== wanted[k],
      );
      if (changing.some((k) => wanted[k]) && view.calendar.blocked === "flag_off")
        return problem(
          403,
          "connection_path_disabled",
          "Tool connections are not turned on for this company",
          "An owner or admin turns on crew mail and calendar in Team › Connections › Paths",
        );
      for (const k of changing) {
        const id = wanted[k];
        if (!id) continue;
        const ok =
          k === "calendar" &&
          listOf(connections, p.company.id).some(
            (c) =>
              c.id === id &&
              c.scope === "person" &&
              c.owner_user_id === userOf(p) &&
              c.provider === "google" &&
              (c.state === "healthy" || c.state === "rate_limited") &&
              c.tiers.some((t) => t === "calendar_read" || t === "calendar_write"),
          );
        // Mail: Gmail read waits on Google's security review, as the demo's server does.
        if (!ok)
          return problem(
            422,
            "grant_not_allowed",
            "That memory is not the owner's to grant",
            k === "mail"
              ? "Gmail read waits on Google's security review (a founder input)"
              : "Choose one of your own working Google connections that can read your calendar",
          );
      }
      const t = at();
      for (const k of changing) {
        const old = live(k);
        if (old) Object.assign(old, { revoked_at: t, updated_at: t });
        if (wanted[k])
          grants.push({
            company_id: p.company.id,
            id: uid(0x9c80, ++seq),
            crew_membership_id: crew,
            kind: k,
            connection_id: wanted[k] as string,
            granted_by_membership_id: owner,
            revoked_at: null,
            actor_kind: "person",
            actor_membership_id: owner,
            created_at: t,
            updated_at: t,
          });
      }
      return json(crewView(p, crew));
    }),
    "connections.oauthStart": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      // M4-04: a reconnect names one of the caller's own live connections.
      const again = url.searchParams.get("reconnect");
      if (again) {
        const row = listOf(connections, p.company.id).find((c) => c.id === again);
        if (!row || !mayManage(p, row)) return notFound();
        if (row.state === "disconnected" || row.provider !== params.provider)
          return problem(
            409,
            "connection_state_conflict",
            "This connection is disconnected",
            "Only a live connection of this service and scope can be reconnected",
          );
      }
      const provider = PROVIDERS.find((x) => x.key === params.provider);
      return problem(
        409,
        "connection_not_set_up",
        "This provider needs admin setup (the OAuth app credentials) before anyone connects it",
        `An admin sets ${(provider?.setup_env ?? []).join(", ")} on the server first`,
      );
    },
    "connections.oauthComplete": ({ principal: p }) =>
      p
        ? problem(
            409,
            "connection_not_set_up",
            "This provider needs admin setup (the OAuth app credentials) before anyone connects it",
          )
        : notFound(),
  };
}
